// Dual-Space View — Problem-Solution Map
// Full page: FilterBar + SpacePanels + Detail/Coach tabs + ValuePropBar

import { useState, useCallback, useMemo, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { useDualSpace } from '@/hooks/useDualSpace'
import { useCrossLinks } from '@/hooks/useCrossLinks'
import { useDragToLink } from '@/hooks/useDragToLink'
import { useAIStore } from '@/stores/aiStore'
import { FilterBar } from './FilterBar'
import { SpacePanel } from './SpacePanel'
import { DetailPanel } from './DetailPanel'
import { ValuePropBar } from './ValuePropBar'
import { DragHintBar } from './DragHintBar'
import { DualSpaceCoachPanel } from './DualSpaceCoachPanel'
import { AddEntityDialog } from './AddEntityDialog'
import { dualSpaceApi } from '@/lib/api/client'
import type { SpaceNode, ValuePropChain } from '@ispot/shared'

interface DualSpacePageProps {
  accountId: string
  projectId: string
}

export function DualSpacePage({ accountId, projectId }: DualSpacePageProps) {
  const {
    data, isLoading, error,
    selectedNode, selectedNodeId, highlightedIds,
    selectNode, personas, jtbds,
    personaFilter, jtbdFilter, setPersonaFilter, setJtbdFilter, clearFilters,
    reload,
  } = useDualSpace(accountId, projectId)

  const { createLink, deleteLink } = useCrossLinks(accountId, projectId, reload)

  const {
    isDragging, dragSource, dropTargetId,
    canDrag, isValidDropTarget,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
  } = useDragToLink((sourceType, sourceId, targetType, targetId) => {
    createLink({ sourceType, sourceId, targetType, targetId })
  })

  // Right panel tab: details or coach
  const [rightTab, setRightTab] = useState<'details' | 'coach'>('details')

  // Add entity dialog
  const [addDialog, setAddDialog] = useState<{ open: boolean; type: 'need' | 'benefit'; parent: SpaceNode | null }>({
    open: false, type: 'need', parent: null,
  })

  // API key from AI store
  const apiKey = useAIStore(s => s.apiKey)

  // Expanded nodes — auto-expand first two levels on load
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!data) return
    const ids = new Set<string>()
    for (const node of [...data.problemSpace, ...data.solutionSpace]) {
      ids.add(node.id)
      for (const child of node.children) ids.add(child.id)
    }
    setExpandedIds(ids)
  }, [data])

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Value prop chain
  const valuePropChain: ValuePropChain | null = useMemo(() => {
    if (!data) return null
    const persona = data.problemSpace[0]
    if (!persona) return { isComplete: false }
    const jtbd = persona.children.find(c => c.type === 'jtbd')
    const need = jtbd?.children.find(c => c.type === 'need' && c.crossLinkCount > 0)
    const cap = need
      ? data.solutionSpace.find(s => need.crossLinkIds.includes(s.id))
      : data.solutionSpace[0]
    const feat = cap?.children.find(c => c.type === 'feature')
    const ben = feat?.children.find(c => c.type === 'benefit')
      || cap?.children.find(c => c.type === 'benefit')
    const slot = (n?: SpaceNode) => n ? { id: n.id, title: n.title } : undefined
    return {
      persona: slot(persona), need: slot(need), capability: slot(cap),
      feature: slot(feat), benefit: slot(ben),
      isComplete: !!(persona && need && cap && feat && ben),
    }
  }, [data])

  // Handle add entity submission
  const handleAddEntity = useCallback(async (entity: { statement: string; parentId: string; entityType: 'need' | 'benefit' }) => {
    if (entity.entityType === 'need') {
      const parentNode = selectedNode
      if (!parentNode) return
      // Find the persona session ID by walking up
      const personaId = findAncestorId(data?.problemSpace || [], parentNode.id, 'persona')
      if (personaId) {
        await dualSpaceApi.createNeed(accountId, projectId, {
          personaSessionId: personaId,
          jtbdId: entity.parentId,
          statement: entity.statement,
        })
      }
    } else {
      await dualSpaceApi.createBenefit(accountId, projectId, {
        featureId: entity.parentId,
        statement: entity.statement,
      })
    }
    reload()
  }, [accountId, projectId, selectedNode, data, reload])

  // Accept coach-suggested link
  const handleAcceptLink = useCallback((linkData: { sourceType: string; sourceId: string; targetType: string; targetId: string }) => {
    createLink(linkData)
  }, [createLink])

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 text-warm-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-text-muted mb-2">{error}</p>
          <button onClick={reload} className="text-[13px] text-core hover:underline">Retry</button>
        </div>
      </div>
    )
  }

  const dragProps = {
    canDrag, isValidDropTarget,
    onDragStart: handleDragStart, onDragOver: handleDragOver,
    onDragLeave: handleDragLeave, onDrop: handleDrop, onDragEnd: handleDragEnd,
  }

  return (
    <div className="flex flex-col h-full">
      <DragHintBar visible={isDragging} sourceTitle={dragSource?.title} />

      <FilterBar
        personas={personas}
        jtbds={jtbds}
        personaFilter={personaFilter}
        jtbdFilter={jtbdFilter}
        onPersonaChange={setPersonaFilter}
        onJtbdChange={setJtbdFilter}
        onClear={clearFilters}
        stats={data?.stats || null}
      />

      <div className="flex flex-1 overflow-hidden">
        <SpacePanel
          title="Problem space"
          subtitle="who encounters what?"
          space="problem"
          nodes={data?.problemSpace || []}
          selectedNodeId={selectedNodeId}
          highlightedIds={highlightedIds}
          expandedIds={expandedIds}
          dropTargetId={dropTargetId}
          addLabel="Persona"
          onSelect={selectNode}
          onToggleExpand={toggleExpand}
          {...dragProps}
        />

        <div className="w-px bg-border flex-shrink-0" />

        <SpacePanel
          title="Solution space"
          subtitle="solved by what?"
          space="solution"
          nodes={data?.solutionSpace || []}
          selectedNodeId={selectedNodeId}
          highlightedIds={highlightedIds}
          expandedIds={expandedIds}
          dropTargetId={dropTargetId}
          addLabel="Capability"
          onSelect={selectNode}
          onToggleExpand={toggleExpand}
          {...dragProps}
        />

        {/* Right panel: Detail / Coach tabs */}
        <div className="w-[300px] border-l border-border bg-warm-100 flex flex-col flex-shrink-0 overflow-hidden">
          <div className="flex border-b border-border-subtle flex-shrink-0">
            <button
              onClick={() => setRightTab('details')}
              className={`flex-1 text-[11px] font-medium py-2.5 text-center transition-colors ${rightTab === 'details' ? 'text-text border-b-2 border-core' : 'text-text-muted hover:text-text-secondary'}`}
            >
              Details
            </button>
            <button
              onClick={() => setRightTab('coach')}
              className={`flex-1 text-[11px] font-medium py-2.5 text-center transition-colors ${rightTab === 'coach' ? 'text-text border-b-2 border-core' : 'text-text-muted hover:text-text-secondary'}`}
            >
              Coach
            </button>
          </div>

          {rightTab === 'details' ? (
            <DetailPanel
              selectedNode={selectedNode}
              data={data}
              onUnlink={deleteLink}
              embedded
            />
          ) : (
            <DualSpaceCoachPanel
              accountId={accountId}
              projectId={projectId}
              selectedNode={selectedNode}
              data={data}
              apiKey={apiKey}
              onAcceptLink={handleAcceptLink}
              onCreateNeed={async (personaSessionId, jtbdId, statement) => {
                await dualSpaceApi.createNeed(accountId, projectId, { personaSessionId, jtbdId, statement })
                reload()
              }}
            />
          )}
        </div>
      </div>

      <ValuePropBar chain={valuePropChain} />

      <AddEntityDialog
        open={addDialog.open}
        entityType={addDialog.type}
        parentNode={addDialog.parent}
        onClose={() => setAddDialog({ open: false, type: 'need', parent: null })}
        onSubmit={handleAddEntity}
      />
    </div>
  )
}

/** Walk trees to find an ancestor node of a given type */
function findAncestorId(trees: SpaceNode[], targetId: string, ancestorType: string): string | null {
  function walk(node: SpaceNode, ancestors: SpaceNode[]): string | null {
    if (node.id === targetId) {
      const ancestor = ancestors.find(a => a.type === ancestorType)
      return ancestor?.id || null
    }
    for (const child of node.children) {
      const found = walk(child, [...ancestors, node])
      if (found) return found
    }
    return null
  }
  for (const root of trees) {
    const found = walk(root, [])
    if (found) return found
  }
  return null
}
